import { AppConstants } from './app-constants';
import {
  FormattedMaintenanceItem,
  GroupedMaintenancePackages,
  MaintenanceLabelType,
  MaintenanceLineItem,
  PackagesData,
} from '../typings/general';

/**
 * Groups an array of maintenance items by their interval value.
 *
 * @param PackagesData[] data - The array of maintenance items to group.
 * @returns Record<string, PackagesData[]>
 * An object where the keys are the interval values (e.g., "10000")
 * and the values are arrays of maintenance items with that interval.
 */
export const groupMaintenanceItemsByInterval = (
  data: PackagesData[] = [],
): Record<string, PackagesData[]> => {
  return data.reduce(
    (acc: Record<string, PackagesData[]>, item: PackagesData) => {
      const intervalValue = item.interval.value;
      if (!acc[intervalValue]) {
        acc[intervalValue] = [];
      }
      acc[intervalValue].push(item);
      return acc;
    },
    {},
  );
};

export const getIntervalPosition = (
  interval: number,
  data: Record<string, PackagesData[]>,
) => {
  const intervals = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b);

  let index = intervals.findIndex((i) => i >= interval);

  if (index === -1) {
    index = intervals.length;
  }

  const isIntervalOutOfRange = !intervals.includes(interval);

  return {
    intervals,
    index,
    isIntervalOutOfRange,
  };
};

/**
 * Filters an object of maintenance items grouped by interval to return the current, previous, and next intervals
 * around a given interval.
 *
 * @param Record<string, PackagesData[]> data - An object where keys are interval strings (e.g., "10000")
 *   and values are arrays of `PackagesData` (which is assumed to be synonymous with `PackageItem`).
 * @param number interval - The interval to search for.
 * @returns Record<string, PackagesData[]>
 * An object containing the current, previous, and next intervals,
 * or an empty object if the interval is not found.
 */
export const getMaintenanceItemsByInterval = (
  data: Record<string, PackagesData[]>,
  interval: number,
): Record<string, PackagesData[]> => {
  const { intervals, index } = getIntervalPosition(interval, data);
  const maintenanceItems: Record<string, PackagesData[]> = {};
  let isLastItem: boolean = false;

  if (index === -1) return maintenanceItems;

  // Get the highest interval value (the last one)
  const lastIntervalValue = intervals[intervals.length - 1];

  const addInterval = (i: number) => {
    if (i >= 0 && i < intervals.length) {
      const intervalStr = String(intervals[i]);
      if (data[intervalStr]) {
        // Check if this is the highest interval in the data
        const isHighestInterval = Number(intervalStr) === lastIntervalValue;

        maintenanceItems[intervalStr] = data[intervalStr].map(item => ({
          ...item,
          isLastMaintenance: isHighestInterval
        }));
      }
    }
  };

  // Special case for 10000 interval - only include current and next
  if (interval <= 10000) {
    addInterval(index);     // Current (10000)
    addInterval(index + 1); // Next (20000)
  }
  // For first interval (other than 10000), include current, next, and next+1
  else if (index === 0) {
    addInterval(0);
    addInterval(1);
    addInterval(2);
  }
  // Special case for last interval
  else if (isLastItem) {
    addInterval(index - 1); // Previous interval
    addInterval(index);     // Last interval (current)
  }
  // Normal case - include previous, current, and next
  else {
    addInterval(index - 1);
    addInterval(index);
    addInterval(index + 1);
  }

  return maintenanceItems
};

/**
 * Adds the isLastMaintenance flag to maintenance data when no specific mileage is provided.
 * This function marks the items in the highest interval with isLastMaintenance=true.
 * 
 * @param data - An object containing maintenance items organized by interval
 * @returns The same data structure with isLastMaintenance flags added
 */
export const addLastMaintenanceFlag = (
  data: Record<string, PackagesData[]>
): Record<string, PackagesData[]> => {
  // If no data, return empty object
  if (!data || Object.keys(data).length === 0) return {};

  // Get all intervals and find the highest one
  const intervals = Object.keys(data).map(Number).sort((a, b) => a - b);
  const highestInterval = intervals[intervals.length - 1];

  const modifiedData: Record<string, PackagesData[]> = {};

  for (const intervalStr of Object.keys(data)) {
    const isHighestInterval = Number(intervalStr) === highestInterval;

    modifiedData[intervalStr] = data[intervalStr].map(item => ({
      ...item,
      isLastMaintenance: isHighestInterval
    }));
  }

  return modifiedData;
};

export const formatIntervalValue = (interval: number) => {
  return `${interval.toLocaleString()} mi`;
};

/**
 * Filters an array of package items to include only those with specific descriptions.
 *
 * @param {MaintenanceLineItem[]} packages - The array of package items to filter.
 * @returns {MaintenanceLineItem[]} - An array of package items with descriptions matching "Minor Maintenance", "Standard Maintenance", "Standard Maintenance - small", or "Extended Maintenance".
 */
export const filterPackagesByDescription = (
  packages: MaintenanceLineItem[],
  filterByAllowedDescriptions = true,
): MaintenanceLineItem[] => {
  const allowedDescriptions = [
    'Minor Maintenance',
    'Standard Maintenance',
    'Standard Maintenance - small',
    'Extended Maintenance',
  ];

  return packages.filter((pkg) =>
    filterByAllowedDescriptions
      ? allowedDescriptions.includes(pkg.description)
      : !allowedDescriptions.includes(pkg.description),
  );
};

/**
 * Formats an array of maintenance item strings according to the specified rules.
 *
 * @param {string[]} items - The array of maintenance item strings.
 * @returns {FormattedMaintenanceItem[]} - An array of formatted maintenance item objects, alphabetically ordered by action.
 */
const formatMaintenanceItems = (
  items: string[],
): FormattedMaintenanceItem[] => {
  const formattedItems: FormattedMaintenanceItem[] = items.map((item) => {
    const parts = item.split(',');
    if (parts.length === 2) {
      const action = parts[1].trim();
      const partsOfVehicle = parts[0].trim();
      return { action: action, parts: partsOfVehicle, fullText: item };
    } else {
      // No comma or more than one comma, display in a single line
      return { action: item, parts: '', fullText: item };
    }
  });

  formattedItems.sort((a, b) => a.action.localeCompare(b.action));

  return formattedItems;
};

/**
 * Determines the combined maintenance label based on the given package descriptions.
 *
 * @param {MaintenanceLabelType[]} descriptions - An array of package descriptions.
 * @returns {MaintenanceLabelType} - The combined maintenance label.
 */
const determineCombinedLabel = (
  descriptions: MaintenanceLabelType[],
): MaintenanceLabelType => {
  if (descriptions.length === 1) {
    return descriptions[0];
  } else if (descriptions.length > 1) {
    if (descriptions.includes('Extended Maintenance')) {
      return 'Extended Maintenance';
    } else if (descriptions.includes('Standard Maintenance')) {
      return 'Standard Maintenance';
    } else {
      return descriptions[0];
    }
  } else {
    throw new Error('No descriptions provided');
  }
};

/**
 * Formats a maintenance item string, replacing hyphens with empty spaces.
 *
 * @param {string} item - The maintenance item string to format.
 * @returns {string} - The formatted maintenance item string.
 */
const formatNoteText = (item: string): string => {
  return item.replace(/-/g, '');
};

/**
 * Extracts and formats the notes from an array of package items, combining the labels.
 *
 * @param {PackageItem[]} packages - The array of package items.
 * @returns {{ combinedLabel: MaintenanceLabelType | undefined, formattedNotes: FormattedMaintenanceItem[] }} - An object containing the combined label and the formatted notes.
 */
export const groupAndFormatPackageNotes = (
  packages: MaintenanceLineItem[],
): GroupedMaintenancePackages => {
  const filteredPackages = filterPackagesByDescription(packages);

  const descriptions = filteredPackages.map(
    (pkg) => pkg.description,
  ) as MaintenanceLabelType[];

  const combinedLabel =
    descriptions.length > 0 ? determineCombinedLabel(descriptions) : undefined;

  const formattedNotes: Record<string, FormattedMaintenanceItem[]> =
    filteredPackages.reduce(
      (
        acc: Record<string, FormattedMaintenanceItem[]>,
        pkg: MaintenanceLineItem,
      ) => {
        const formattedNotesArray = pkg.notes.map((note) =>
          formatNoteText(note),
        );
        acc[pkg.description] = formatMaintenanceItems(formattedNotesArray);
        return acc;
      },
      {},
    );

  return { combinedLabel, formattedNotes };
};

export const viewAllRecommendedServices = (
  maintenanceItems: Record<string, FormattedMaintenanceItem[]>,
  isDesktop: boolean,
) => {
  const maintenanceItemsSize = isDesktop
    ? AppConstants.DESKTOP_MAINTEANCE_ITEMS_SIZE
    : AppConstants.DEFAULT_MAINTENANCE_ITEMS_SIZE;
  for (const key in maintenanceItems) {
    if (maintenanceItems.hasOwnProperty(key)) {
      const value = maintenanceItems[key];

      if (typeof value === 'string' || Array.isArray(value)) {
        if (value.length > maintenanceItemsSize) {
          return true;
        }
      } else if (typeof value === 'number') {
        if (String(value).length > maintenanceItemsSize) {
          return true;
        }
      } else if (typeof value === 'object' && value !== null) {
        if (JSON.stringify(value).length > maintenanceItemsSize) {
          return true;
        }
      }
    }
  }
  return false;
};